import { Box } from "@rebass/grid";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  DisplayText,
  ESpacings,
  Modal,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import styled from "@rentiohq/web-theme";
import { useNavigate } from "react-router-dom";
import {
  isCandidacySubmission,
  shouldShowIncome,
} from "utils/application.utils";
import { Rating } from "../../../../../components/Rating";
import * as t from "../../../../../services/translationService";
import { RM_COLORS } from "../rentmatch.colors";
import { ApplicationInfoIndicators } from "./ApplicationTable/applicationIndicators";
import { getApplicationIndicators } from "./ApplicationTable/applicationIndicators.utils";
import { WarningTooltip } from "./WarningTooltip";

export interface IOtherApplicationProperty {
  applicationId: string;
  propertyId: number | null;
  propertyAddress: string;
  step: number;
  stepCompletedAt: string | null;
  stepStartedAt: string | null;
}

export interface IOtherApplicationNote {
  applicationId: string;
  content: string | null;
  createdAt: string | Date;
}

interface IProps {
  candidateName: string;
  properties: IOtherApplicationProperty[];
  /** Notes across the broker's own properties, matched to a row by id. */
  notes?: IOtherApplicationNote[];
  onClose: () => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

// The header, the rows and their text follow the candidate list rather than
// this dialog's own taste: same size, same weight, same gray, no uppercase and
// no letter-spacing, a 2px rule under the header and a 1px rule under each row.
// A broker reads the same facts in both places, so they should not be styled
// like two different products (DEV-3122).
const Th = styled.th`
  padding: 12px 16px 12px 0;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral90};
  background: ${({ theme }) => theme.colors.neutral0};
  border-bottom: 2px solid ${({ theme }) => theme.getColor("gray", 200)};

  &:last-child {
    padding-right: 0;
  }
`;

const Tr = styled.tr<{ isClickable?: boolean }>`
  cursor: ${({ isClickable }) => (isClickable ? "pointer" : "default")};

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  }

  &:hover td {
    background: ${({ theme }) => theme.colors.neutral10};
  }
`;

const Td = styled.td`
  padding: 16px 16px 16px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral90};
  vertical-align: middle;

  &:last-child {
    padding-right: 0;
  }
`;

// The property stands where the candidate's name stands in the list, so it
// takes that treatment.
const AddressText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutral100};
`;

interface IStatusBadgeProps {
  step: number;
  isArchived?: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  waitingList: { bg: RM_COLORS.yellowBg, color: RM_COLORS.yellowDark },
  notEligible: { bg: RM_COLORS.redBg, color: RM_COLORS.redDark },
  candidateTenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  tenant: { bg: RM_COLORS.greenBgSoft, color: RM_COLORS.greenDark },
  visitor: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  invited: { bg: RM_COLORS.blueBgAlt, color: RM_COLORS.blueDark },
  planAsVisitor: { bg: RM_COLORS.skyBg, color: RM_COLORS.blueDeep },
  default: { bg: RM_COLORS.gray100, color: RM_COLORS.gray700 },
};

const getStatusColors = (step: number, isArchived?: boolean) => {
  // An archived candidacy is not in a phase any more, so it gets the neutral
  // treatment rather than the color of wherever it stood before (DEV-3122).
  if (isArchived) {
    return STATUS_COLORS.default;
  }

  if (step === EIntakeStep.waitingList) return STATUS_COLORS.waitingList;
  if (step === EIntakeStep.notEligible) return STATUS_COLORS.notEligible;
  if (step === EIntakeStep.candidateTenant)
    return STATUS_COLORS.candidateTenant;
  if (step === EIntakeStep.tenant) return STATUS_COLORS.tenant;
  if (step === EIntakeStep.visitor) return STATUS_COLORS.visitor;
  if (step === EIntakeStep.invited) return STATUS_COLORS.invited;
  if (step === EIntakeStep.planAsVisitor) return STATUS_COLORS.planAsVisitor;
  return STATUS_COLORS.default;
};

// Every measurable property taken from the pill in the candidate list: the
// same display, the same gap, the same 3px above it, the same 2px 8px padding,
// 11px at weight 600, the same fully rounded corners, and fit-content so it
// hugs its text instead of filling the column.
//
// One deliberate difference: the list paints its pill green whatever the phase,
// because there the phase is the column you are already standing in. Here the
// rows span several phases at once, so the color carries the phase.
const StatusBadge = styled.span<IStatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  width: fit-content;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 9999px;
  background-color: ${({ step, isArchived }) =>
    getStatusColors(step, isArchived).bg};
  color: ${({ step, isArchived }) => getStatusColors(step, isArchived).color};
`;

// Date over status, the way the Activity column reads in the list.
const ActivityCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DateText = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.neutral90};
`;

// The same chevron the candidate list puts at the end of a row: a bordered
// button in gray, not a bare blue arrow. Rounded on all four corners here,
// where the list rounds only the right side because it sits attached to the
// action buttons beside it.
const NavigateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${({ theme }) => theme.colors.neutral40};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.neutral0};
  color: ${({ theme }) => theme.colors.neutral70};
  cursor: pointer;
  flex-shrink: 0;
  transition: background 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.colors.neutral10};
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
  }
`;

const AddressCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IncomeText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral100};
`;

const formatActivityDate = (property: IOtherApplicationProperty): string => {
  const dateValue = property.stepCompletedAt ?? property.stepStartedAt;
  if (!dateValue) {
    return "-";
  }

  return utils.date.format(new Date(dateValue), "dd/MM/yyyy HH:mm");
};

/**
 * One row of the dialog: the property, and what this candidate delivered there.
 *
 * The values are fetched per row rather than carried on the cross-property
 * meta, which only holds an address, a step and two timestamps. Fetching them
 * means they come from the same serializer the candidate list reads, so the
 * rules agreed on DEV-3075, DEV-3107, DEV-3114, DEV-3115 and DEV-3135 apply
 * here by construction instead of being restated (DEV-3122).
 *
 * Nothing here is red on account of the match score. That score belongs to a
 * property, and every row is a different one; decided by Jonas on 2026-09-06.
 * The document fraction still reddens, since that is not score-based.
 */
const getStatusLabel = (params: {
  step: number;
  isArchived: boolean;
  showsSubmittedLabel: boolean;
}) => {
  const { step, isArchived, showsSubmittedLabel } = params;

  if (isArchived) {
    return getLocalizedText("applications.tab.archived");
  }

  if (showsSubmittedLabel) {
    return getLocalizedText("rentmatch.applications.state.submitted");
  }

  return t.applicationStateTitleRentmatch(step as EIntakeStep);
};

const OtherApplicationRow = (props: {
  property: IOtherApplicationProperty;
  notes: IOtherApplicationNote[];
  onNavigate: (propertyId: number, step: number | "archived") => void;
}) => {
  const { property, notes, onNavigate } = props;

  const { detail: application } = applicationHooks.useDetail({
    shouldRefetch: false,
    id: property.applicationId,
  });

  // Archived is not a phase, it is a flag on the application, so it does not
  // travel with the cross-property meta. It arrives with the row's own fetch,
  // the same payload the candidate list reads it from.
  const isArchived = !!application?.archivedAt;

  const canNavigate = property.propertyId != null;
  const navigateToProperty = () => {
    if (property.propertyId != null) {
      onNavigate(property.propertyId, isArchived ? "archived" : property.step);
    }
  };

  const indicators = application
    ? getApplicationIndicators(application)
    : undefined;

  // Same rule as the candidate list: the pill replaces the phase name only
  // where the submission itself was a candidacy and the row stands on the
  // candidate-tenant step.
  const showsSubmittedLabel =
    property.step === EIntakeStep.candidateTenant &&
    isCandidacySubmission(application?.submittedStep);

  return (
    // The whole row navigates, not just the chevron at the end of it. The
    // chevron stays as the affordance that says so, and stops the click from
    // reaching the row so the navigation does not fire twice.
    <Tr
      isClickable={canNavigate}
      onClick={canNavigate ? navigateToProperty : undefined}
    >
      <Td>
        <AddressCell>
          <AddressText>{property.propertyAddress || "-"}</AddressText>
          {notes.length > 0 && (
            <WarningTooltip
              size={16}
              tooltipContent={
                <div style={{ maxWidth: "240px" }}>
                  {notes.map((note, index) => (
                    <div key={index} style={{ marginBottom: "4px" }}>
                      {note.content}
                    </div>
                  ))}
                </div>
              }
            />
          )}
        </AddressCell>
      </Td>
      <Td>
        <ActivityCell>
          <DateText>{formatActivityDate(property)}</DateText>
          {/* Archived wins over the phase: the broker is being told where
              this candidacy is now, and it is in the archive, not in the
              column it sat in before. */}
          <StatusBadge step={property.step} isArchived={isArchived}>
            {getStatusLabel({
              step: property.step,
              isArchived,
              showsSubmittedLabel,
            })}
          </StatusBadge>
        </ActivityCell>
      </Td>
      <Td>
        {indicators && shouldShowIncome(indicators.totalIncome) && (
          <IncomeText>{formatCurrency(indicators.totalIncome)}</IncomeText>
        )}
      </Td>
      <Td>
        {application && <ApplicationInfoIndicators application={application} />}
      </Td>
      <Td>
        <Rating value={application?.rating ?? 0} size={14} disabled={true} />
      </Td>
      <Td style={{ width: 48, textAlign: "right" }}>
        <NavigateButton
          disabled={!canNavigate}
          onClick={event => {
            event.stopPropagation();
            navigateToProperty();
          }}
        >
          <svg
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="1 1 7 6 1 11" />
          </svg>
        </NavigateButton>
      </Td>
    </Tr>
  );
};

export const OtherApplicationsModal = (props: IProps) => {
  const { candidateName, properties, notes = [], onClose } = props;
  const navigate = useNavigate();

  return (
    // Same shape as every other dialog in the dashboard: a plain heading, a
    // dismiss, and the content directly in the body rather than inside a card
    // of its own. Wider than the usual large, because this one is a table and
    // its rows wrapped at 960px (DEV-3122).
    <Modal
      width="extraLarge"
      heading={getLocalizedText("application.other_applications.modal.heading")}
      onClose={onClose}
      hasDismiss={true}
      shouldCloseOnOverlayClick={true}
    >
      <Box mb={ESpacings.loose}>
        <DisplayText size="medium" element="h3">
          {getLocalizedText("system.candidate")}: {candidateName}
        </DisplayText>
      </Box>
      <Table>
        <thead>
          <tr>
            <Th>
              {getLocalizedText(
                "application.other_applications.modal.column.property",
              )}
            </Th>
            <Th>{getLocalizedText("activity_table.info.heading")}</Th>
            <Th>{getLocalizedText("intake.financial.title")}</Th>
            <Th>{getLocalizedText("system.more_info")}</Th>
            <Th>{getLocalizedText("property.rating")}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {properties.map(property => (
            <OtherApplicationRow
              key={property.applicationId}
              property={property}
              notes={notes.filter(
                note => note.applicationId === property.applicationId,
              )}
              onNavigate={(propertyId, step) => {
                onClose();
                // The applications screen takes its tab from the router state,
                // so the broker lands on the phase this candidacy is in rather
                // than on the first tab. Step 0 means the meta had no phase row
                // to report, and then the screen decides for itself.
                navigate(`/properties/${propertyId}/applications`, {
                  state: step === "archived" || step > 0 ? { step } : undefined,
                });
              }}
            />
          ))}
        </tbody>
      </Table>
    </Modal>
  );
};
